import { Vue, Component, Ref, Prop, Emit } from 'vue-property-decorator'
import AlertBanner from '@/components/common/AlertBanner/alert-banner.vue'
import { configurationBillableConceptService } from '@/modules/configurationModule/detail/accounting/billable/concepts/services/configuration-billable-concept.service'
import { StrictNullable } from '@/shared/types/app.types'
import { ConfigurationBillableConceptResponse } from '@/modules/configurationModule/detail/accounting/billable/concepts/services/configuration-billable-concept.dto'
import { CommonParams } from '@/api/resources/common/shared/models/common.models'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { ConfigurationLocalAccountingRateGridConceptsModalBasicFormState, initialLocalAccountingRateGridConceptsModalBasicFormState } from '@/modules/configurationModule/detail/local-accounting/billable/rates/components/rates-modal-concepts-grid-basic-form/configuration-local-accounting-billable-rate-modal-concepts-grid-basic-form.model'
import { BillableRateBillableConceptCreateRequest, BillableRateBillableConceptPatchInBatchRequest, BillableRateBillableConceptResponse } from '@/modules/configurationModule/detail/local-accounting/billable/rates/services/billable-rate-billable-concept/billable-rate-billable-concept.dto'
import { BModal } from 'bootstrap-vue'
import { BillableRateResponse } from '@/modules/configurationModule/detail/local-accounting/billable/rates/services/configuration-local-accounting-billable-rates/billable-rate.dto'
import { EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import { billableRateBillableConceptService } from '@/modules/configurationModule/detail/local-accounting/billable/rates/services/billable-rate-billable-concept/billable-rate-billable-concept.service'

@Component({
    components: {
        AlertBanner
    }
})
export default class ConfigurationLocalAccountingBillableRateModalConceptsGridBasicForm extends Vue {
    @Ref() readonly ratesModalRateGridConceptsBasicForm!: BModal
    @Prop(String) readonly actionTitle!: string
    @Prop() readonly rate!: BillableRateResponse

    @Emit()
    create(createdRate: BillableRateBillableConceptResponse): BillableRateBillableConceptResponse {
        return createdRate
    }

    @Emit()
    update(): void {}

    state: ConfigurationLocalAccountingRateGridConceptsModalBasicFormState
    constructor() {
        super()
        this.state = initialLocalAccountingRateGridConceptsModalBasicFormState()
    }

    get isUpdatedForm(): boolean {
        return !!this.state.concept?.conceptId
    }

    get modelValidated(): boolean {
        const conceptIdExists = !!this.state.concept.conceptSelected?.id
        const referenceExists = !!this.state.concept.reference?.length
        const aliasExists = !!this.state.concept.alias?.length
        const realValueExists = this.state.concept.referenceValue >= 0
        const billableValueExists = this.state.concept.billableValue >= 0
        const hasErrors: boolean = this.$validator.errors.any() || !conceptIdExists || !referenceExists || !aliasExists || !realValueExists || !billableValueExists
        const isDirty: boolean = this.$_someFieldIsDirty(this.fields)
        return !hasErrors && isDirty
    }

    get visibleAdvice(): boolean {
        return this.isUpdatedForm
    }

    get conceptHasCopayment(): boolean {
        return this.state.concept.hasCopayments
    }

    get canAddCopayment(): boolean {
        return this.rate.allowsCopayment
    }

    async show(concept: StrictNullable<BillableRateBillableConceptResponse>): Promise<void> {
        this.state = {
            ...initialLocalAccountingRateGridConceptsModalBasicFormState(),
            laboratoryId: this.$store.getters.labId
        }
        await this._getConcepts()
        if (concept) {
            this.state.concept = {
                ...this.state.concept,
                conceptId: concept.id,
                conceptSelected: concept.concept,
                groupConcept: concept.concept.group.name,
                code: concept.concept.code,
                reference: concept.referenceCode,
                alias: concept.alias,
                referenceValue: concept.referenceValue,
                billableValue: concept.billableValue,
                active: concept.active,
                hasCopayments: concept.hasCopayments,
                requiresAuthorization: concept.requiresAuthorization,
                copaymentValue: concept.copaymentValue !== null ? concept.copaymentValue : 0
            }
        }
        this.state.concept.unit = this.rate.accountingUnit.name
        this.ratesModalRateGridConceptsBasicForm.show()
    }

    hide(): void {
        this.ratesModalRateGridConceptsBasicForm.hide()
    }

    onConceptChange(concept: ConfigurationBillableConceptResponse): void {
        this._setCodeAndGroupConcept(concept)
    }

    onChangeHasCopayments(): void {
        const valueHasCopayments = !this.state.concept.hasCopayments
        if (!valueHasCopayments) {
            this.state.concept.copaymentValue = 0
        }
    }

    async doAction(): Promise<void> {
        if (this.isUpdatedForm) {
            const rateConceptsUpdateOperation = await this._updateOperation()
            if (rateConceptsUpdateOperation) {
                // emits an event
                this.update()
                await this.$validator.reset()
            }
        } else {
            const rateConceptsCreateOperation = await this._createOperation()
            if (rateConceptsCreateOperation) {
                // emits an event
                this.create(rateConceptsCreateOperation)
                await this.$validator.reset()
            }
        }
    }

    private async _addRateConcepts(
        request: BillableRateBillableConceptCreateRequest
    ): Promise<StrictNullable<BillableRateBillableConceptResponse>> {
        if (request) {
            const response = await this.$_toCallSecure(billableRateBillableConceptService.create(
                this.state.laboratoryId,
                request
            ))
            if (response) {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated'))
                this.hide()
                return response
            }
        }
        return null
    }

    private async _updateRateConcepts(
        request: BillableRateBillableConceptPatchInBatchRequest
    ): Promise<StrictNullable<EntityIdentifierWithErrorResponse[]>> {
        if (request) {
            const response = await this.$_toCallSecure(billableRateBillableConceptService.patch(
                this.state.laboratoryId,
                [request]
            ))
            if (response) {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated'))
                this.hide()
                return response
            }
        }
        return null
    }

    /// FORM OPERATIONS ///
    private _createOperation(): Promise<StrictNullable<BillableRateBillableConceptResponse>> {
        return this._addRateConcepts(
            {
                rateId: this.rate.id,
                conceptId: this.state.concept.conceptSelected?.id!,
                referenceCode: this.state.concept.reference!,
                alias: this.state.concept.alias!,
                referenceValue: this.state.concept.referenceValue,
                billableValue: this.state.concept.billableValue,
                requiresAuthorization: this.state.concept.requiresAuthorization,
                active: this.state.concept.active,
                hasCopayments: this.state.concept.hasCopayments,
                copaymentValue: this.conceptHasCopayment ? this.state.concept.copaymentValue : null
            }
        )
    }

    private _updateOperation(): Promise<StrictNullable<EntityIdentifierWithErrorResponse[]>> {
        return this._updateRateConcepts(
            {
                id: this.state.concept.conceptId!,
                referenceCode: this.state.concept.reference!,
                alias: this.state.concept.alias!,
                referenceValue: this.state.concept.referenceValue,
                billableValue: this.state.concept.billableValue,
                requiresAuthorization: this.state.concept.requiresAuthorization,
                active: this.state.concept.active,
                hasCopayments: this.state.concept.hasCopayments,
                copaymentValue: this.conceptHasCopayment ? this.state.concept.copaymentValue : null
            }
        )
    }

    private _setCodeAndGroupConcept(concept: ConfigurationBillableConceptResponse): void {
        this.state.concept = {
            ...this.state.concept,
            code: concept.code,
            groupConcept: concept.group.name,
            reference: concept.code,
            alias: concept.name
        }
    }

    private async _getConcepts(): Promise<void> {
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const filterModel = {
            'active': {
                filterType: 'boolean',
                type: 'equals',
                filter: true
            }
        }
        const response = await this.$_toCallSecure(configurationBillableConceptService.paginator(filterModel, params))
        this.state.concepts = response ? response.content : []
    }
}
